/**
 * CustomizedProductModel custom element
 * Displays swatch images for selected product options based on configured enabled values
 * Subscribes to variant selection changes via PubSub
 */

// ============================================================================
// CONFIGURATION CONSTANTS
// These values must match those in the Liquid templates:
// - personalization-line-controls.liquid (text size, curvature)
// - customized-product-model.liquid (position, line spacing, image size, viewBox)
// ============================================================================

// Text size configuration
const DEFAULT_LINE1_SIZE = 17;
const DEFAULT_LINE2_SIZE = 7;
const MIN_TEXT_SIZE = 1;
const MAX_TEXT_SIZE = 40;

// Curvature configuration
const DEFAULT_CURVATURE = 0;
const MIN_CURVATURE = -100;
const MAX_CURVATURE = 100;

// Line spacing configuration
const DEFAULT_LINE_SPACING = 0;
const MIN_LINE_SPACING = -40;
const MAX_LINE_SPACING = 40;

// Image size configuration
const DEFAULT_IMAGE_SIZE = 100;
const MIN_IMAGE_SIZE = 30;
const MAX_IMAGE_SIZE = 100;

// Position offset configuration
const DEFAULT_POSITION_OFFSET = 50;
const MIN_POSITION_OFFSET = 10;
const MAX_POSITION_OFFSET = 90;

// ViewBox and generated file dimensions
const VIEWBOX_WIDTH = 1024;
const VIEWBOX_HEIGHT = 1024;

class CustomizedProductModel extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.imageElement = null;
    this.overlayLine1 = null;
    this.overlayLine2 = null;
    this.overlayLine1TextPath = null;
    this.overlayLine2TextPath = null;
    this.curvePath1 = null;
    this.curvePath2 = null;
    this.overlaySvg = null;
    this.inputLine1 = null;
    this.inputLine2 = null;
    this.inputSize1 = null;
    this.inputSize2 = null;
    this.curvatureSlider1 = null;
    this.curvatureSlider2 = null;
    this.curvatureValue1 = null;
    this.curvatureValue2 = null;
    this.buttonAddLine = null;
    this.addLineWrapper = null;
    this.line2Container = null;
    this.loadingSpinner = null;
    this.verticalSlider = null;
    this.horizontalSlider = null;
    this.verticalPositionValue = null;
    this.horizontalPositionValue = null;
    this.verticalOffset = DEFAULT_POSITION_OFFSET;
    this.horizontalOffset = DEFAULT_POSITION_OFFSET;
    this.clickOutsideHandler = null;
    this.initializedLines = new Set();
    this.swatchData = {};
    this.sectionId = null;
    this.unsubscriber = null;
    this.previewBlob = null;
    this.previewGenerationId = 0;
    this.currentFont1 = 'allura';
    this.currentFont2 = 'allura';
    this.isBold1 = false;
    this.isBold2 = false;
    this.fileInput = null;
    this.generatedFileHeight = VIEWBOX_HEIGHT;
    this.generatedFileWidth = VIEWBOX_WIDTH;
    this.resizeObserver = null;
    this.showAdvancedOptions = false;
    this.advancedOptionsButton = null;
    this.verticalSliderWrapper = null;
    this.horizontalSliderWrapper = null;
    this.lineSpacingSlider = null;
    this.lineSpacingValue = null;
    this.lineSpacingWrapper = null;
    this.lineSpacing = DEFAULT_LINE_SPACING;
    this.curvature1 = DEFAULT_CURVATURE;
    this.curvature2 = DEFAULT_CURVATURE;
    this.fontDataCache = {};
    this.fontUrls = {};
    this.imageSizeSlider = null;
    this.imageSizeValue = null;
    this.imageSizeWrapper = null;
    this.imageSize = DEFAULT_IMAGE_SIZE;
  }

  connectedCallback() {
    // Get section ID
    this.sectionId = this.dataset.sectionId;
    if (!this.sectionId) {
      console.error('CustomizedProductModel: Missing section ID');
      return;
    }

    // Get DOM elements
    this.container = this.querySelector('.customized-product-model__accordion');
    this.imageElement = this.querySelector('.customized-product-model__image');
    this.overlayLine1 = this.querySelector('.customized-product-model__overlay-text[data-type="line1"]');
    this.overlayLine2 = this.querySelector('.customized-product-model__overlay-text[data-type="line2"]');
    this.overlayLine1TextPath = this.overlayLine1?.querySelector('textPath');
    this.overlayLine2TextPath = this.overlayLine2?.querySelector('textPath');
    this.curvePath1 = this.querySelector('#curve-line1-' + this.sectionId);
    this.curvePath2 = this.querySelector('#curve-line2-' + this.sectionId);
    this.inputLine1 = this.querySelector('input[data-personalization-field="line1"]');
    this.inputLine2 = this.querySelector('input[data-personalization-field="line2"]');
    this.inputSize1 = this.querySelector('input[data-personalization-field="size1"]');
    this.inputSize2 = this.querySelector('input[data-personalization-field="size2"]');
    this.curvatureSlider1 = this.querySelector('#curvature-slider-line1-' + this.sectionId);
    this.curvatureSlider2 = this.querySelector('#curvature-slider-line2-' + this.sectionId);
    this.curvatureValue1 = this.querySelector('#curvature-value-line1-' + this.sectionId);
    this.curvatureValue2 = this.querySelector('#curvature-value-line2-' + this.sectionId);
    this.buttonAddLine = this.querySelector('#add-line-' + this.sectionId);
    this.addLineWrapper = this.querySelector('#add-line-' + this.sectionId + '-wrapper');
    this.line2Container = this.querySelector('.personalization-line2-container');
    this.loadingSpinner = this.querySelector('.customized-product-model__loading');
    this.overlaySvg = this.querySelector('.customized-product-model__overlay-svg');
    this.verticalSlider = this.querySelector('#vertical-slider-' + this.sectionId);
    this.horizontalSlider = this.querySelector('#horizontal-slider-' + this.sectionId);
    this.verticalPositionValue = this.querySelector('#vertical-position-value-' + this.sectionId);
    this.horizontalPositionValue = this.querySelector('#horizontal-position-value-' + this.sectionId);
    this.advancedOptionsButton = this.querySelector('#advanced-options-' + this.sectionId);
    this.verticalSliderWrapper = this.querySelector('.customized-product-model__vertical-slider-wrapper');
    this.horizontalSliderWrapper = this.querySelector('.customized-product-model__horizontal-slider-wrapper');
    this.lineSpacingSlider = this.querySelector('#line-spacing-slider-' + this.sectionId);
    this.lineSpacingValue = this.querySelector('#line-spacing-value-' + this.sectionId);
    this.lineSpacingWrapper = this.querySelector('.customized-product-model__line-spacing-wrapper');
    this.imageSizeSlider = this.querySelector('#image-size-slider-' + this.sectionId);
    this.imageSizeValue = this.querySelector('#image-size-value-' + this.sectionId);
    this.imageSizeWrapper = this.querySelector('.customized-product-model__image-size-wrapper');

    if (!this.container || !this.imageElement) {
      console.error('CustomizedProductModel: Missing required DOM elements');
      return;
    }

    // Set up input listeners for personalization fields
    if (this.inputLine1 && this.overlayLine1) {
      this.inputLine1.addEventListener('input', (e) => {
        this.updateOverlayText('line1', e.target.value);
      });
    }

    if (this.inputLine2 && this.overlayLine2) {
      this.inputLine2.addEventListener('input', (e) => {
        this.updateOverlayText('line2', e.target.value);
      });
    }

    // Set up personalization controls for each line
    this.setupPersonalizationLine(1);
    
    // Set up "add line" button
    if (this.buttonAddLine && this.line2Container && this.addLineWrapper) {
      this.buttonAddLine.addEventListener('click', () => {
        this.line2Container.removeAttribute('hidden');
        this.addLineWrapper.setAttribute('hidden', '');
        // Initialize line 2 controls when it becomes visible
        if (!this.initializedLines.has(2)) {
          this.setupPersonalizationLine(2);
        }
      });
    }

    // Set up position sliders
    this.setupPositionSliders();

    // Set up image size slider
    this.setupImageSizeSlider();

    // Set up advanced options button
    if (this.advancedOptionsButton && this.verticalSliderWrapper && this.horizontalSliderWrapper) {
      // Initially hide the sliders, line spacing, and image size
      this.verticalSliderWrapper.style.display = 'none';
      this.horizontalSliderWrapper.style.display = 'none';
      if (this.lineSpacingWrapper) {
        this.lineSpacingWrapper.style.display = 'none';
      }
      if (this.imageSizeWrapper) {
        this.imageSizeWrapper.style.display = 'none';
      }
      this.showAdvancedOptions = false;
      
      this.advancedOptionsButton.addEventListener('click', () => {
        this.showAdvancedOptions = !this.showAdvancedOptions;
        
        // Toggle sliders, line spacing, and image size visibility
        if (this.showAdvancedOptions) {
          this.verticalSliderWrapper.style.display = '';
          this.horizontalSliderWrapper.style.display = '';
          if (this.lineSpacingWrapper) {
            this.lineSpacingWrapper.style.display = '';
          }
          if (this.imageSizeWrapper) {
            this.imageSizeWrapper.style.display = '';
          }
          this.advancedOptionsButton.classList.add('customized-button--selected');
          this.advancedOptionsButton.classList.remove('customized-button--noselected');
        } else {
          this.verticalSliderWrapper.style.display = 'none';
          this.horizontalSliderWrapper.style.display = 'none';
          if (this.lineSpacingWrapper) {
            this.lineSpacingWrapper.style.display = 'none';
          }
          if (this.imageSizeWrapper) {
            this.imageSizeWrapper.style.display = 'none';
          }
          this.advancedOptionsButton.classList.remove('customized-button--selected');
          this.advancedOptionsButton.classList.add('customized-button--noselected');
        }
      });
      
      // Set initial button state
      this.advancedOptionsButton.classList.remove('customized-button--selected');
      this.advancedOptionsButton.classList.add('customized-button--noselected');
    }

    // Set up size input listeners
    if (this.inputSize1) {
      this.inputSize1.addEventListener('change', () => {
        this.updateOverlaySizes();
        this.generatePreviewImage();
      });
    }

    if (this.inputSize2) {
      this.inputSize2.addEventListener('change', () => {
        this.updateOverlaySizes();
        this.generatePreviewImage();
      });
    }

    // Set up size button listeners
    const sizeButtons = this.querySelectorAll('[data-action^="increase-size"], [data-action^="decrease-size"]');
    sizeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const action = button.dataset.action;
        const target = button.dataset.target;
        const input = target === 'line1' ? this.inputSize1 : this.inputSize2;
        
        if (input) {
          const currentValue = parseInt(input.value) || (target === 'line1' ? DEFAULT_LINE1_SIZE : DEFAULT_LINE2_SIZE);
          const min = parseInt(input.min) || MIN_TEXT_SIZE;
          const max = parseInt(input.max) || MAX_TEXT_SIZE;
          
          if (action === 'increase-size' && currentValue < max) {
            input.value = currentValue + 1;
            this.updateOverlaySizes();
            this.generatePreviewImage();
          } else if (action === 'decrease-size' && currentValue > min) {
            input.value = currentValue - 1;
            this.updateOverlaySizes();
            this.generatePreviewImage();
          }
        }
      });
    });

    // Set up line spacing slider
    this.setupLineSpacingSlider();

    // Create the file input early and add it to the form
    this.createFileInput();

    // Set up form submission listener
    this.setupFormSubmission();

    // Parse swatch data
    try {
      const swatchDataRaw = this.dataset.swatchData;
      this.swatchData = JSON.parse(swatchDataRaw);
    } catch (e) {
      console.error('CustomizedProductModel: Failed to parse swatch data', e);
      this.swatchData = {};
    }

    // Parse font URLs
    try {
      const fontUrlsRaw = this.dataset.fontUrls;
      this.fontUrls = JSON.parse(fontUrlsRaw);
    } catch (e) {
      console.error('CustomizedProductModel: Failed to parse font URLs', e);
      this.fontUrls = {};
    }

    // Subscribe to variant selection changes
    this.unsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleVariantChange.bind(this));

    // Check initial state
    this.updateDisplay();
    
    // Set initial overlay sizes
    this.updateOverlaySizes();
    
    // Set initial line spacing
    this.updateLineSpacing();
    
    // Set initial curvature
    this.updateOverlayCurvature();
    
    // Update overlay sizes on container resize to keep px values in sync
    this.resizeObserver = new ResizeObserver(() => {
      this.updateOverlaySizes();
    });
    
    if (this.imageElement?.parentElement) {
      this.resizeObserver.observe(this.imageElement.parentElement);
    }
  }

  disconnectedCallback() {
    if (this.unsubscriber) {
      this.unsubscriber();
    }
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Set up personalization controls for a specific line
   * @param {number} lineNumber - Line number (1 or 2)
   */
  setupPersonalizationLine(lineNumber) {
    const lineNum = lineNumber.toString();
    const lineSuffix = 'line' + lineNum;
    const overlayElement = lineNumber === 1 ? this.overlayLine1 : this.overlayLine2;
    
    if (!overlayElement) return;
    
    // Prevent duplicate initialization
    if (this.initializedLines.has(lineNumber)) return;
    this.initializedLines.add(lineNumber);

    // Get elements for this line
    const buttonFont = this.querySelector('#font-' + lineSuffix + '-' + this.sectionId);
    const dropdownFont = this.querySelector('#font-dropdown-' + lineSuffix + '-' + this.sectionId);
    const buttonTextSize = this.querySelector('#text-size-' + lineSuffix + '-' + this.sectionId);
    const dropdownTextSize = this.querySelector('#text-size-dropdown-' + lineSuffix + '-' + this.sectionId);
    const buttonBold = this.querySelector('#bold-' + lineSuffix + '-' + this.sectionId);
    const inputFont = this.querySelector('input[data-personalization-field="font' + lineNum + '"]');
    const inputBold = this.querySelector('input[data-personalization-field="bold' + lineNum + '"]');
    const inputSize = this.querySelector('input[data-personalization-field="size' + lineNum + '"]');
    
    // Set up font dropdown
    if (buttonFont && dropdownFont) {
      // Function to update selected state in dropdown
      const updateSelectedFont = (selectedFontValue) => {
        const fontItems = dropdownFont.querySelectorAll('.font-dropdown__item');
        fontItems.forEach(item => {
          if (item.dataset.fontValue === selectedFontValue) {
            item.classList.add('selected');
          } else {
            item.classList.remove('selected');
          }
        });
      };

      // Toggle dropdown on button click
      buttonFont.addEventListener('click', (e) => {
        e.stopPropagation();
        const isHidden = dropdownFont.hasAttribute('hidden');
        if (isHidden) {
          const currentFont = lineNumber === 1 ? this.currentFont1 : this.currentFont2;
          updateSelectedFont(currentFont);
          dropdownFont.removeAttribute('hidden');
          buttonFont.setAttribute('aria-expanded', 'true');
        } else {
          dropdownFont.setAttribute('hidden', '');
          buttonFont.setAttribute('aria-expanded', 'false');
        }
      });

      // Handle font selection from dropdown
      const fontItems = dropdownFont.querySelectorAll('.font-dropdown__item');
      fontItems.forEach(item => {
        item.addEventListener('click', async (e) => {
          e.stopPropagation();
          const fontValue = item.dataset.fontValue;
          
          // Update hidden input
          if (inputFont) {
            inputFont.value = fontValue;
          }
          
          // Update current font
          if (lineNumber === 1) {
            this.currentFont1 = fontValue;
          } else {
            this.currentFont2 = fontValue;
          }
          
          // Update selected state in dropdown
          updateSelectedFont(fontValue);
          
          // Update overlay font
          this.updateOverlayFont(lineSuffix, fontValue);
          
          // Close dropdown
          dropdownFont.setAttribute('hidden', '');
          buttonFont.setAttribute('aria-expanded', 'false');
          
          // Show loading spinner and wait for font to load
          await this.loadFontAndGeneratePreview(fontValue);
        });
      });

      // Set initial font
      const initialFontValue = inputFont ? inputFont.value : 'allura';
      if (lineNumber === 1) {
        this.currentFont1 = initialFontValue;
      } else {
        this.currentFont2 = initialFontValue;
      }
      this.updateOverlayFont(lineSuffix, initialFontValue);
      updateSelectedFont(initialFontValue);
    }

    // Set up text size dropdown
    if (buttonTextSize && dropdownTextSize) {
      buttonTextSize.addEventListener('click', (e) => {
        e.stopPropagation();
        const isHidden = dropdownTextSize.hasAttribute('hidden');
        if (isHidden) {
          dropdownTextSize.removeAttribute('hidden');
          buttonTextSize.setAttribute('aria-expanded', 'true');
        } else {
          dropdownTextSize.setAttribute('hidden', '');
          buttonTextSize.setAttribute('aria-expanded', 'false');
        }
      });
    }

    // Set up curvature dropdown and slider
    const buttonCurvature = this.querySelector('#curvature-' + lineSuffix + '-' + this.sectionId);
    const dropdownCurvature = this.querySelector('#curvature-dropdown-' + lineSuffix + '-' + this.sectionId);
    
    if (buttonCurvature && dropdownCurvature) {
      buttonCurvature.addEventListener('click', (e) => {
        e.stopPropagation();
        const isHidden = dropdownCurvature.hasAttribute('hidden');
        if (isHidden) {
          dropdownCurvature.removeAttribute('hidden');
          buttonCurvature.setAttribute('aria-expanded', 'true');
        } else {
          dropdownCurvature.setAttribute('hidden', '');
          buttonCurvature.setAttribute('aria-expanded', 'false');
        }
      });
    }
    
    // Set up curvature slider
    this.setupCurvatureSlider(lineNumber);

    // Set up bold button
    if (buttonBold && inputBold) {
      buttonBold.addEventListener('click', () => {
        const isBold = lineNumber === 1 ? this.isBold1 : this.isBold2;
        const newBoldState = !isBold;
        
        // Update state
        if (lineNumber === 1) {
          this.isBold1 = newBoldState;
        } else {
          this.isBold2 = newBoldState;
        }
        
        // Update hidden input for form submission
        inputBold.value = newBoldState ? 'Oui' : '';
        
        // Update button selected state
        if (newBoldState) {
          buttonBold.classList.add('customized-button--selected');
          buttonBold.classList.remove('customized-button--noselected');
        } else {
          buttonBold.classList.remove('customized-button--selected');
          buttonBold.classList.add('customized-button--noselected');
        }
        
        // Update overlay bold state
        this.updateOverlayBold(lineSuffix, newBoldState);
        
        // Generate preview image
        this.generatePreviewImage();
      });
      
      // Set initial bold state
      if (lineNumber === 1) {
        this.isBold1 = false;
        this.updateOverlayBold('line1', false);
      } else {
        this.isBold2 = false;
        this.updateOverlayBold('line2', false);
      }
      
      // Set initial button state
      buttonBold.classList.remove('customized-button--selected');
      buttonBold.classList.add('customized-button--noselected');
    }

    // Set up remove button for line 2
    if (lineNumber === 2) {
      const buttonRemove = this.querySelector('#remove-line2-' + this.sectionId);
      if (buttonRemove && this.line2Container && this.addLineWrapper && this.inputLine2) {
        buttonRemove.addEventListener('click', () => {
          // Reset line 2 input
          this.inputLine2.value = '';
          this.updateOverlayText('line2', '');
          
          // Hide line 2 container
          this.line2Container.setAttribute('hidden', '');
          
          // Show add line wrapper
          this.addLineWrapper.removeAttribute('hidden');
          
          // Reset line 2 state
          this.currentFont2 = 'allura';
          this.isBold2 = false;
          this.curvature2 = DEFAULT_CURVATURE;
          
          // Reset curvature slider
          if (this.curvatureValue2) {
            this.curvatureValue2.value = String(DEFAULT_CURVATURE);
          }
          if (this.curvatureSlider2) {
            const handle = this.curvatureSlider2.querySelector('.customized-product-model__slider-handle');
            if (handle) {
              const percentage = ((DEFAULT_CURVATURE - MIN_CURVATURE) / (MAX_CURVATURE - MIN_CURVATURE)) * 100;
              handle.style.left = `${percentage}%`;
              handle.style.transform = 'translateY(-50%) translateX(-50%)';
            }
            this.curvatureSlider2.setAttribute('aria-valuenow', String(DEFAULT_CURVATURE));
          }
          
          // Update overlay
          this.updateOverlayFont('line2', 'allura');
          this.updateOverlayBold('line2', false);
          this.updateOverlayCurvature();
          
          // Generate preview image
          this.generatePreviewImage();
        });
      }
    }

    // Set up click outside handler (only once for all dropdowns)
    if (!this.clickOutsideHandler) {
      this.clickOutsideHandler = (e) => {
        // Handle all font dropdowns
        const fontButtons = this.querySelectorAll('[data-action="toggle-font-dropdown"]');
        fontButtons.forEach(button => {
          const buttonId = button.id;
          const lineMatch = buttonId.match(/font-(line\d+)-/);
          if (lineMatch) {
            const dropdownId = 'font-dropdown-' + lineMatch[1] + '-' + this.sectionId;
            const dropdown = this.querySelector('#' + dropdownId);
            if (dropdown && 
                !button.contains(e.target) && 
                !dropdown.contains(e.target)) {
              dropdown.setAttribute('hidden', '');
              button.setAttribute('aria-expanded', 'false');
            }
          }
        });
        
        // Handle all text size dropdowns
        const textSizeButtons = this.querySelectorAll('[data-action="toggle-text-size-dropdown"]');
        textSizeButtons.forEach(button => {
          const buttonId = button.id;
          const lineMatch = buttonId.match(/text-size-(line\d+)-/);
          if (lineMatch) {
            const dropdownId = 'text-size-dropdown-' + lineMatch[1] + '-' + this.sectionId;
            const dropdown = this.querySelector('#' + dropdownId);
            if (dropdown && 
                !button.contains(e.target) && 
                !dropdown.contains(e.target)) {
              dropdown.setAttribute('hidden', '');
              button.setAttribute('aria-expanded', 'false');
            }
          }
        });
        
        // Handle all curvature dropdowns
        const curvatureButtons = this.querySelectorAll('[data-action="toggle-curvature-dropdown"]');
        curvatureButtons.forEach(button => {
          const buttonId = button.id;
          const lineMatch = buttonId.match(/curvature-(line\d+)-/);
          if (lineMatch) {
            const dropdownId = 'curvature-dropdown-' + lineMatch[1] + '-' + this.sectionId;
            const dropdown = this.querySelector('#' + dropdownId);
            if (dropdown && 
                !button.contains(e.target) && 
                !dropdown.contains(e.target)) {
              dropdown.setAttribute('hidden', '');
              button.setAttribute('aria-expanded', 'false');
            }
          }
        });
      };
      document.addEventListener('click', this.clickOutsideHandler);
    }
  }

  /**
   * Set up position sliders for overlay positioning
   */
  setupPositionSliders() {
    // Initialize offsets from hidden inputs if they exist
    if (this.verticalPositionValue && this.verticalPositionValue.value) {
      const val = parseFloat(this.verticalPositionValue.value) || DEFAULT_POSITION_OFFSET;
      this.verticalOffset = Math.max(MIN_POSITION_OFFSET, Math.min(MAX_POSITION_OFFSET, val));
    } else {
      this.verticalOffset = DEFAULT_POSITION_OFFSET;
    }
    if (this.horizontalPositionValue && this.horizontalPositionValue.value) {
      const val = parseFloat(this.horizontalPositionValue.value) || DEFAULT_POSITION_OFFSET;
      this.horizontalOffset = Math.max(MIN_POSITION_OFFSET, Math.min(MAX_POSITION_OFFSET, val));
    } else {
      this.horizontalOffset = DEFAULT_POSITION_OFFSET;
    }

    // Update overlay position on slider change
    const updateOverlayPosition = () => {
      // Update curve paths to reflect position changes
      this.updateCurvePathPositions();
    };

    // Set up custom slider drag functionality
    const setupSliderDrag = (slider, isVertical) => {
      const handle = slider.querySelector('.customized-product-model__slider-handle');
      if (!handle) return;

      let isDragging = false;
      let dragOffset = 0;
      const minValue = MIN_POSITION_OFFSET;
      const maxValue = MAX_POSITION_OFFSET;

      const updateSliderValue = (value) => {
        const clampedValue = Math.max(minValue, Math.min(maxValue, value));
        
        if (isVertical) {
          this.verticalOffset = clampedValue;
          if (this.verticalPositionValue) {
            this.verticalPositionValue.value = clampedValue.toString();
          }
        } else {
          this.horizontalOffset = clampedValue;
          if (this.horizontalPositionValue) {
            this.horizontalPositionValue.value = clampedValue.toString();
          }
        }

        // Update handle position
        const percentage = ((clampedValue - minValue) / (maxValue - minValue)) * 100;
        
        if (isVertical) {
          handle.style.top = `${percentage}%`;
          handle.style.transform = 'translateX(-50%) translateY(-50%) rotate(90deg)';
        } else {
          handle.style.left = `${percentage}%`;
          handle.style.transform = 'translateY(-50%) translateX(-50%)';
        }

        // Update aria attributes
        slider.setAttribute('aria-valuenow', clampedValue.toString());
        
        updateOverlayPosition();
        this.generatePreviewImage();
      };

      const getValueFromPosition = (clientX, clientY) => {
        const rect = slider.getBoundingClientRect();
        let percentage;
        
        if (isVertical) {
          const y = clientY - rect.top - dragOffset;
          percentage = (y / rect.height) * 100;
        } else {
          const x = clientX - rect.left - dragOffset;
          percentage = (x / rect.width) * 100;
        }
        
        // Clamp percentage to 0-100
        percentage = Math.max(0, Math.min(100, percentage));
        
        // Convert to value range (30-70)
        return minValue + (percentage / 100) * (maxValue - minValue);
      };

      const handleStart = (e) => {
        isDragging = true;
        slider.setAttribute('aria-disabled', 'false');
        
        const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
        const clientY = e.type === 'mousedown' ? e.clientY : e.touches[0].clientY;
        
        // If clicking on slider track, move handle to that position
        if (e.target === slider || e.target.classList.contains('customized-product-model__slider-handle')) {
          // Handle click on slider or handle
          if (e.target === slider) {
            // Clicked on track area (slider itself)
            dragOffset = 0;
            const value = getValueFromPosition(clientX, clientY);
            updateSliderValue(value);
          } else {
            // Clicked on handle, calculate offset for smooth dragging
            const rect = slider.getBoundingClientRect();
            const handleRect = handle.getBoundingClientRect();
            
            if (isVertical) {
              dragOffset = clientY - (handleRect.top + handleRect.height / 2);
            } else {
              dragOffset = clientX - (handleRect.left + handleRect.width / 2);
            }
          }
        } else {
          // Clicked elsewhere, treat as track click
          dragOffset = 0;
          const value = getValueFromPosition(clientX, clientY);
          updateSliderValue(value);
        }
        
        e.preventDefault();
      };

      const handleMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        
        const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
        const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
        const value = getValueFromPosition(clientX, clientY);
        updateSliderValue(value);
      };

      const handleEnd = () => {
        isDragging = false;
        dragOffset = 0;
      };

      // Mouse events
      slider.addEventListener('mousedown', handleStart);
      handle.addEventListener('mousedown', handleStart);
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);

      // Touch events
      slider.addEventListener('touchstart', handleStart, { passive: false });
      handle.addEventListener('touchstart', handleStart, { passive: false });
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);

      // Keyboard support
      slider.addEventListener('keydown', (e) => {
        let newValue;
        const step = 1;
        
        if (isVertical) {
          if (e.key === 'ArrowUp') {
            newValue = this.verticalOffset - step;
          } else if (e.key === 'ArrowDown') {
            newValue = this.verticalOffset + step;
          } else {
            return;
          }
        } else {
          if (e.key === 'ArrowLeft') {
            newValue = this.horizontalOffset - step;
          } else if (e.key === 'ArrowRight') {
            newValue = this.horizontalOffset + step;
          } else {
            return;
          }
        }
        
        e.preventDefault();
        updateSliderValue(newValue);
      });

      // Initialize handle position
      const initialValue = isVertical ? this.verticalOffset : this.horizontalOffset;
      updateSliderValue(initialValue);
    };

    // Set up both sliders
    if (this.verticalSlider) {
      setupSliderDrag(this.verticalSlider, true);
    }

    if (this.horizontalSlider) {
      setupSliderDrag(this.horizontalSlider, false);
    }

    // Set up reset buttons
    const resetVerticalButton = this.querySelector('#reset-vertical-position-' + this.sectionId);
    const resetHorizontalButton = this.querySelector('#reset-horizontal-position-' + this.sectionId);

    if (resetVerticalButton && this.verticalSlider) {
      resetVerticalButton.addEventListener('click', () => {
        const handle = this.verticalSlider.querySelector('.customized-product-model__slider-handle');
        if (handle) {
          this.verticalOffset = DEFAULT_POSITION_OFFSET;
          if (this.verticalPositionValue) {
            this.verticalPositionValue.value = String(DEFAULT_POSITION_OFFSET);
          }
          // Update handle position
          handle.style.top = '50%';
          handle.style.transform = 'translateX(-50%) translateY(-50%) rotate(90deg)';
          this.verticalSlider.setAttribute('aria-valuenow', '50');
          updateOverlayPosition();
          this.generatePreviewImage();
        }
      });
    }

    if (resetHorizontalButton && this.horizontalSlider) {
      resetHorizontalButton.addEventListener('click', () => {
        const handle = this.horizontalSlider.querySelector('.customized-product-model__slider-handle');
        if (handle) {
          this.horizontalOffset = DEFAULT_POSITION_OFFSET;
          if (this.horizontalPositionValue) {
            this.horizontalPositionValue.value = String(DEFAULT_POSITION_OFFSET);
          }
          // Update handle position
          handle.style.left = '50%';
          handle.style.transform = 'translateY(-50%) translateX(-50%)';
          this.horizontalSlider.setAttribute('aria-valuenow', '50');
          updateOverlayPosition();
          this.generatePreviewImage();
        }
      });
    }

    // Set initial overlay position
    updateOverlayPosition();
  }

  /**
   * Set up image size slider for controlling the model image size
   */
  setupImageSizeSlider() {
    // Initialize image size from hidden input if it exists
    if (this.imageSizeValue && this.imageSizeValue.value) {
      const val = parseFloat(this.imageSizeValue.value) || DEFAULT_IMAGE_SIZE;
      this.imageSize = Math.max(MIN_IMAGE_SIZE, Math.min(MAX_IMAGE_SIZE, val));
    } else {
      this.imageSize = DEFAULT_IMAGE_SIZE;
    }

    // Update image size function - scales only the image, not the overlay
    const updateImageSize = () => {
      if (this.imageElement) {
        this.imageElement.style.width = `${this.imageSize}%`;
        this.imageElement.style.height = 'auto';
      }
    };

    // Set up custom slider drag functionality
    if (this.imageSizeSlider) {
      const handle = this.imageSizeSlider.querySelector('.customized-product-model__slider-handle');
      if (!handle) return;

      let isDragging = false;
      let dragOffset = 0;
      const minValue = MIN_IMAGE_SIZE;
      const maxValue = MAX_IMAGE_SIZE;

      const updateSliderValue = (value) => {
        const clampedValue = Math.max(minValue, Math.min(maxValue, value));
        
        this.imageSize = clampedValue;
        if (this.imageSizeValue) {
          this.imageSizeValue.value = clampedValue.toString();
        }

        // Update handle position
        const percentage = ((clampedValue - minValue) / (maxValue - minValue)) * 100;
        handle.style.left = `${percentage}%`;
        handle.style.transform = 'translateY(-50%) translateX(-50%)';

        // Update aria attributes
        this.imageSizeSlider.setAttribute('aria-valuenow', clampedValue.toString());
        
        updateImageSize();
        this.generatePreviewImage();
      };

      const getValueFromPosition = (clientX) => {
        const rect = this.imageSizeSlider.getBoundingClientRect();
        let percentage;
        
        const x = clientX - rect.left - dragOffset;
        percentage = (x / rect.width) * 100;
        
        // Clamp percentage to 0-100
        percentage = Math.max(0, Math.min(100, percentage));
        
        // Convert to value range
        return minValue + (percentage / 100) * (maxValue - minValue);
      };

      const handleStart = (e) => {
        isDragging = true;
        this.imageSizeSlider.setAttribute('aria-disabled', 'false');
        
        const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
        
        if (e.target === this.imageSizeSlider || e.target.classList.contains('customized-product-model__slider-handle')) {
          if (e.target === this.imageSizeSlider) {
            dragOffset = 0;
            const value = getValueFromPosition(clientX);
            updateSliderValue(value);
          } else {
            const rect = this.imageSizeSlider.getBoundingClientRect();
            const handleRect = handle.getBoundingClientRect();
            dragOffset = clientX - (handleRect.left + handleRect.width / 2);
          }
        } else {
          dragOffset = 0;
          const value = getValueFromPosition(clientX);
          updateSliderValue(value);
        }
        
        e.preventDefault();
      };

      const handleMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        
        const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
        const value = getValueFromPosition(clientX);
        updateSliderValue(value);
      };

      const handleEnd = () => {
        isDragging = false;
        dragOffset = 0;
      };

      // Mouse events
      this.imageSizeSlider.addEventListener('mousedown', handleStart);
      handle.addEventListener('mousedown', handleStart);
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);

      // Touch events
      this.imageSizeSlider.addEventListener('touchstart', handleStart, { passive: false });
      handle.addEventListener('touchstart', handleStart, { passive: false });
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);

      // Keyboard support
      this.imageSizeSlider.addEventListener('keydown', (e) => {
        let newValue;
        const step = 1;
        
        if (e.key === 'ArrowLeft') {
          newValue = this.imageSize - step;
        } else if (e.key === 'ArrowRight') {
          newValue = this.imageSize + step;
        } else {
          return;
        }
        
        e.preventDefault();
        updateSliderValue(newValue);
      });

      // Initialize handle position and apply initial size
      updateSliderValue(this.imageSize);
    }

    // Set up reset button
    const resetImageSizeButton = this.querySelector('#reset-image-size-' + this.sectionId);
    if (resetImageSizeButton && this.imageSizeSlider) {
      resetImageSizeButton.addEventListener('click', () => {
        const handle = this.imageSizeSlider.querySelector('.customized-product-model__slider-handle');
        if (handle) {
          this.imageSize = DEFAULT_IMAGE_SIZE;
          if (this.imageSizeValue) {
            this.imageSizeValue.value = String(DEFAULT_IMAGE_SIZE);
          }
          // Update handle position (100% = far right)
          const percentage = ((DEFAULT_IMAGE_SIZE - MIN_IMAGE_SIZE) / (MAX_IMAGE_SIZE - MIN_IMAGE_SIZE)) * 100;
          handle.style.left = `${percentage}%`;
          handle.style.transform = 'translateY(-50%) translateX(-50%)';
          this.imageSizeSlider.setAttribute('aria-valuenow', String(DEFAULT_IMAGE_SIZE));
          
          // Update image size - scale only the image
          if (this.imageElement) {
            this.imageElement.style.width = '100%';
            this.imageElement.style.height = 'auto';
          }
          this.generatePreviewImage();
        }
      });
    }

    // Apply initial image size
    updateImageSize();
  }

  /**
   * Set up line spacing slider for controlling the spacing between text lines
   */
  setupLineSpacingSlider() {
    // Initialize line spacing from hidden input if it exists
    if (this.lineSpacingValue && this.lineSpacingValue.value) {
      const val = parseFloat(this.lineSpacingValue.value) || DEFAULT_LINE_SPACING;
      this.lineSpacing = Math.max(MIN_LINE_SPACING, Math.min(MAX_LINE_SPACING, val));
    } else {
      this.lineSpacing = DEFAULT_LINE_SPACING;
    }

    // Update line spacing function
    const updateLineSpacingFromSlider = () => {
      this.updateLineSpacing();
      this.generatePreviewImage();
    };

    // Set up custom slider drag functionality
    if (this.lineSpacingSlider) {
      const handle = this.lineSpacingSlider.querySelector('.customized-product-model__slider-handle');
      if (!handle) return;

      let isDragging = false;
      let dragOffset = 0;
      const minValue = MIN_LINE_SPACING;
      const maxValue = MAX_LINE_SPACING;

      const updateSliderValue = (value) => {
        const clampedValue = Math.max(minValue, Math.min(maxValue, Math.round(value)));
        
        this.lineSpacing = clampedValue;
        if (this.lineSpacingValue) {
          this.lineSpacingValue.value = clampedValue.toString();
        }

        // Update handle position
        const percentage = ((clampedValue - minValue) / (maxValue - minValue)) * 100;
        handle.style.left = `${percentage}%`;
        handle.style.transform = 'translateY(-50%) translateX(-50%)';

        // Update aria attributes
        this.lineSpacingSlider.setAttribute('aria-valuenow', clampedValue.toString());
        
        updateLineSpacingFromSlider();
      };

      const getValueFromPosition = (clientX) => {
        const rect = this.lineSpacingSlider.getBoundingClientRect();
        let percentage;
        
        const x = clientX - rect.left - dragOffset;
        percentage = (x / rect.width) * 100;
        
        // Clamp percentage to 0-100
        percentage = Math.max(0, Math.min(100, percentage));
        
        // Convert to value range
        return minValue + (percentage / 100) * (maxValue - minValue);
      };

      const handleStart = (e) => {
        isDragging = true;
        this.lineSpacingSlider.setAttribute('aria-disabled', 'false');
        
        const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
        
        if (e.target === this.lineSpacingSlider || e.target.classList.contains('customized-product-model__slider-handle')) {
          if (e.target === this.lineSpacingSlider) {
            dragOffset = 0;
            const value = getValueFromPosition(clientX);
            updateSliderValue(value);
          } else {
            const handleRect = handle.getBoundingClientRect();
            dragOffset = clientX - (handleRect.left + handleRect.width / 2);
          }
        } else {
          dragOffset = 0;
          const value = getValueFromPosition(clientX);
          updateSliderValue(value);
        }
        
        e.preventDefault();
      };

      const handleMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        
        const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
        const value = getValueFromPosition(clientX);
        updateSliderValue(value);
      };

      const handleEnd = () => {
        isDragging = false;
        dragOffset = 0;
      };

      // Mouse events
      this.lineSpacingSlider.addEventListener('mousedown', handleStart);
      handle.addEventListener('mousedown', handleStart);
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);

      // Touch events
      this.lineSpacingSlider.addEventListener('touchstart', handleStart, { passive: false });
      handle.addEventListener('touchstart', handleStart, { passive: false });
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);

      // Keyboard support
      this.lineSpacingSlider.addEventListener('keydown', (e) => {
        let newValue;
        const step = 1;
        
        if (e.key === 'ArrowLeft') {
          newValue = this.lineSpacing - step;
        } else if (e.key === 'ArrowRight') {
          newValue = this.lineSpacing + step;
        } else {
          return;
        }
        
        e.preventDefault();
        updateSliderValue(newValue);
      });

      // Initialize handle position
      updateSliderValue(this.lineSpacing);
    }

    // Set up reset button
    const resetLineSpacingButton = this.querySelector('#reset-line-spacing-' + this.sectionId);
    if (resetLineSpacingButton && this.lineSpacingSlider) {
      resetLineSpacingButton.addEventListener('click', () => {
        const handle = this.lineSpacingSlider.querySelector('.customized-product-model__slider-handle');
        if (handle) {
          this.lineSpacing = DEFAULT_LINE_SPACING;
          if (this.lineSpacingValue) {
            this.lineSpacingValue.value = String(DEFAULT_LINE_SPACING);
          }
          // Update handle position (0 = center for range -40 to 40)
          const percentage = ((DEFAULT_LINE_SPACING - MIN_LINE_SPACING) / (MAX_LINE_SPACING - MIN_LINE_SPACING)) * 100;
          handle.style.left = `${percentage}%`;
          handle.style.transform = 'translateY(-50%) translateX(-50%)';
          this.lineSpacingSlider.setAttribute('aria-valuenow', String(DEFAULT_LINE_SPACING));
          
          this.updateLineSpacing();
          this.generatePreviewImage();
        }
      });
    }
  }

  /**
   * Set up curvature slider for a specific line
   * @param {number} lineNumber - Line number (1 or 2)
   */
  setupCurvatureSlider(lineNumber) {
    const lineSuffix = 'line' + lineNumber;
    const slider = this.querySelector('#curvature-slider-' + lineSuffix + '-' + this.sectionId);
    const valueInput = this.querySelector('#curvature-value-' + lineSuffix + '-' + this.sectionId);
    
    if (!slider) return;

    // Initialize curvature from hidden input if it exists
    let currentCurvature = DEFAULT_CURVATURE;
    if (valueInput && valueInput.value) {
      const val = parseFloat(valueInput.value) || DEFAULT_CURVATURE;
      currentCurvature = Math.max(MIN_CURVATURE, Math.min(MAX_CURVATURE, val));
    }
    
    if (lineNumber === 1) {
      this.curvature1 = currentCurvature;
    } else {
      this.curvature2 = currentCurvature;
    }

    // Update curvature function
    const updateCurvatureFromSlider = () => {
      this.updateOverlayCurvature();
      this.generatePreviewImage();
    };

    // Set up custom slider drag functionality
    const handle = slider.querySelector('.customized-product-model__slider-handle');
    if (!handle) return;

    let isDragging = false;
    let dragOffset = 0;
    const minValue = MIN_CURVATURE;
    const maxValue = MAX_CURVATURE;

    const updateSliderValue = (value) => {
      const clampedValue = Math.max(minValue, Math.min(maxValue, Math.round(value)));
      
      if (lineNumber === 1) {
        this.curvature1 = clampedValue;
      } else {
        this.curvature2 = clampedValue;
      }
      
      if (valueInput) {
        valueInput.value = clampedValue.toString();
      }

      // Update handle position
      const percentage = ((clampedValue - minValue) / (maxValue - minValue)) * 100;
      handle.style.left = `${percentage}%`;
      handle.style.transform = 'translateY(-50%) translateX(-50%)';

      // Update aria attributes
      slider.setAttribute('aria-valuenow', clampedValue.toString());
      
      updateCurvatureFromSlider();
    };

    const getValueFromPosition = (clientX) => {
      const rect = slider.getBoundingClientRect();
      let percentage;
      
      const x = clientX - rect.left - dragOffset;
      percentage = (x / rect.width) * 100;
      
      // Clamp percentage to 0-100
      percentage = Math.max(0, Math.min(100, percentage));
      
      // Convert to value range
      return minValue + (percentage / 100) * (maxValue - minValue);
    };

    const handleStart = (e) => {
      isDragging = true;
      slider.setAttribute('aria-disabled', 'false');
      
      const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
      
      if (e.target === slider || e.target.classList.contains('customized-product-model__slider-handle')) {
        if (e.target === slider) {
          dragOffset = 0;
          const value = getValueFromPosition(clientX);
          updateSliderValue(value);
        } else {
          const handleRect = handle.getBoundingClientRect();
          dragOffset = clientX - (handleRect.left + handleRect.width / 2);
        }
      } else {
        dragOffset = 0;
        const value = getValueFromPosition(clientX);
        updateSliderValue(value);
      }
      
      e.preventDefault();
    };

    const handleMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      
      const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
      const value = getValueFromPosition(clientX);
      updateSliderValue(value);
    };

    const handleEnd = () => {
      isDragging = false;
      dragOffset = 0;
    };

    // Mouse events
    slider.addEventListener('mousedown', handleStart);
    handle.addEventListener('mousedown', handleStart);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);

    // Touch events
    slider.addEventListener('touchstart', handleStart, { passive: false });
    handle.addEventListener('touchstart', handleStart, { passive: false });
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    // Keyboard support
    slider.addEventListener('keydown', (e) => {
      let newValue;
      const step = 1;
      const currentValue = lineNumber === 1 ? this.curvature1 : this.curvature2;
      
      if (e.key === 'ArrowLeft') {
        newValue = currentValue - step;
      } else if (e.key === 'ArrowRight') {
        newValue = currentValue + step;
      } else {
        return;
      }
      
      e.preventDefault();
      updateSliderValue(newValue);
    });

    // Initialize handle position
    updateSliderValue(currentCurvature);

    // Set up reset button
    const resetButton = this.querySelector('#reset-curvature-' + lineSuffix + '-' + this.sectionId);
    if (resetButton) {
      resetButton.addEventListener('click', () => {
        if (lineNumber === 1) {
          this.curvature1 = DEFAULT_CURVATURE;
        } else {
          this.curvature2 = DEFAULT_CURVATURE;
        }
        
        if (valueInput) {
          valueInput.value = String(DEFAULT_CURVATURE);
        }
        
        // Update handle position (0 = center for range -100 to 100)
        const percentage = ((DEFAULT_CURVATURE - MIN_CURVATURE) / (MAX_CURVATURE - MIN_CURVATURE)) * 100;
        handle.style.left = `${percentage}%`;
        handle.style.transform = 'translateY(-50%) translateX(-50%)';
        slider.setAttribute('aria-valuenow', String(DEFAULT_CURVATURE));
        
        this.updateOverlayCurvature();
        this.generatePreviewImage();
      });
    }
  }

  /**
   * Handle variant selection change event
   * @param {Object} event - The event data from PubSub
   */
  handleVariantChange(event) {
    // Only respond to changes in our section
    const variantSelects = event?.data?.target?.closest('variant-selects');
    if (!variantSelects || !variantSelects.id.includes(this.sectionId)) {
      return;
    }

    this.updateDisplay();
  }

  /**
   * Get all currently selected option values
   * @returns {Array<{value: string, element: HTMLElement}>}
   */
  getSelectedValues() {
    const variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
    if (!variantSelects) {
      return [];
    }

    const selectedValues = [];

    // Get values from radio inputs (swatch selections)
    const radioInputs = variantSelects.querySelectorAll('input[type="radio"]:checked');
    radioInputs.forEach(input => {
      selectedValues.push({
        value: input.value.trim(),
        element: input
      });
    });

    // Get values from select dropdowns
    const selectElements = variantSelects.querySelectorAll('select');
    selectElements.forEach(select => {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption) {
        selectedValues.push({
          value: selectedOption.value.trim(),
          element: selectedOption
        });
      }
    });

    return selectedValues;
  }

  /**
   * Check if any selected value matches enabled values
   * @returns {Object|null} Matched value data or null
   */
  findMatchedValue() {
    const selectedValues = this.getSelectedValues();

    for (const selected of selectedValues) {
      // Check if we have swatch data for this value
      const swatchInfo = this.swatchData[escapeHTML(selected.value)];
      if (swatchInfo && swatchInfo.imageUrl) {
        return {
          value: selected.value,
          swatchInfo: swatchInfo,
          element: selected.element
        };
      }
    }

    return null;
  }

  /**
   * Update the display based on current selections
   */
  updateDisplay() {
    const matchedValue = this.findMatchedValue();

    if (matchedValue) {
      // Update image
      const { imageUrl, imageWidth, imageHeight, pieceJointe } = matchedValue.swatchInfo;
      this.imageElement.src = imageUrl;
      this.imageElement.srcset = `
        ${imageUrl}&width=300 300w,
        ${imageUrl}&width=600 600w,
        ${imageUrl}&width=900 900w
      `.trim();
      this.imageElement.alt = matchedValue.value;
      
      if (imageWidth && imageHeight) {
        this.imageElement.width = imageWidth;
        this.imageElement.height = imageHeight;
      }

      // Show container
      this.container.style.display = '';

      // If piece_jointe is enabled, reset personalization entries
      if (pieceJointe) {
        this.resetPersonalization();
      }
    } else {
      // Hide container and reset all personalization
      this.container.style.display = 'none';
      this.resetPersonalization();
    }
  }

  /**
   * Reset all personalization settings to defaults
   */
  resetPersonalization() {
    // Reset text inputs
    if (this.inputLine1) {
      this.inputLine1.value = '';
      this.updateOverlayText('line1', '');
    }
    if (this.inputLine2) {
      this.inputLine2.value = '';
      this.updateOverlayText('line2', '');
    }

    // Reset font selections
    this.currentFont1 = 'allura';
    this.currentFont2 = 'allura';
    const inputFont1 = this.querySelector('input[data-personalization-field="font1"]');
    const inputFont2 = this.querySelector('input[data-personalization-field="font2"]');
    if (inputFont1) inputFont1.value = 'allura';
    if (inputFont2) inputFont2.value = 'allura';
    this.updateOverlayFont('line1', 'allura');
    this.updateOverlayFont('line2', 'allura');

    // Reset bold state
    this.isBold1 = false;
    this.isBold2 = false;
    const inputBold1 = this.querySelector('input[data-personalization-field="bold1"]');
    const inputBold2 = this.querySelector('input[data-personalization-field="bold2"]');
    if (inputBold1) inputBold1.value = '';
    if (inputBold2) inputBold2.value = '';
    this.updateOverlayBold('line1', false);
    this.updateOverlayBold('line2', false);

    // Reset bold button states
    const buttonBold1 = this.querySelector('#bold-line1-' + this.sectionId);
    const buttonBold2 = this.querySelector('#bold-line2-' + this.sectionId);
    if (buttonBold1) {
      buttonBold1.classList.remove('customized-button--selected');
      buttonBold1.classList.add('customized-button--noselected');
    }
    if (buttonBold2) {
      buttonBold2.classList.remove('customized-button--selected');
      buttonBold2.classList.add('customized-button--noselected');
    }

    // Reset size inputs
    if (this.inputSize1) {
      this.inputSize1.value = String(DEFAULT_LINE1_SIZE);
    }
    if (this.inputSize2) {
      this.inputSize2.value = String(DEFAULT_LINE2_SIZE);
    }

    // Reset curvature
    this.curvature1 = DEFAULT_CURVATURE;
    this.curvature2 = DEFAULT_CURVATURE;
    if (this.curvatureValue1) {
      this.curvatureValue1.value = String(DEFAULT_CURVATURE);
    }
    if (this.curvatureValue2) {
      this.curvatureValue2.value = String(DEFAULT_CURVATURE);
    }
    if (this.curvatureSlider1) {
      const handle = this.curvatureSlider1.querySelector('.customized-product-model__slider-handle');
      if (handle) {
        const percentage = ((DEFAULT_CURVATURE - MIN_CURVATURE) / (MAX_CURVATURE - MIN_CURVATURE)) * 100;
        handle.style.left = `${percentage}%`;
        handle.style.transform = 'translateY(-50%) translateX(-50%)';
      }
      this.curvatureSlider1.setAttribute('aria-valuenow', String(DEFAULT_CURVATURE));
    }
    if (this.curvatureSlider2) {
      const handle = this.curvatureSlider2.querySelector('.customized-product-model__slider-handle');
      if (handle) {
        const percentage = ((DEFAULT_CURVATURE - MIN_CURVATURE) / (MAX_CURVATURE - MIN_CURVATURE)) * 100;
        handle.style.left = `${percentage}%`;
        handle.style.transform = 'translateY(-50%) translateX(-50%)';
      }
      this.curvatureSlider2.setAttribute('aria-valuenow', String(DEFAULT_CURVATURE));
    }

    // Reset line spacing
    this.lineSpacing = DEFAULT_LINE_SPACING;
    if (this.lineSpacingValue) {
      this.lineSpacingValue.value = String(DEFAULT_LINE_SPACING);
    }
    if (this.lineSpacingSlider) {
      const handle = this.lineSpacingSlider.querySelector('.customized-product-model__slider-handle');
      if (handle) {
        const percentage = ((DEFAULT_LINE_SPACING - MIN_LINE_SPACING) / (MAX_LINE_SPACING - MIN_LINE_SPACING)) * 100;
        handle.style.left = `${percentage}%`;
        handle.style.transform = 'translateY(-50%) translateX(-50%)';
      }
      this.lineSpacingSlider.setAttribute('aria-valuenow', String(DEFAULT_LINE_SPACING));
    }

    // Reset position sliders
    this.verticalOffset = DEFAULT_POSITION_OFFSET;
    this.horizontalOffset = DEFAULT_POSITION_OFFSET;
    if (this.verticalPositionValue) {
      this.verticalPositionValue.value = String(DEFAULT_POSITION_OFFSET);
    }
    if (this.horizontalPositionValue) {
      this.horizontalPositionValue.value = String(DEFAULT_POSITION_OFFSET);
    }

    // Reset slider handle positions
    if (this.verticalSlider) {
      const handle = this.verticalSlider.querySelector('.customized-product-model__slider-handle');
      if (handle) {
        handle.style.top = '50%';
        handle.style.transform = 'translateX(-50%) translateY(-50%) rotate(90deg)';
      }
      this.verticalSlider.setAttribute('aria-valuenow', '50');
    }
    if (this.horizontalSlider) {
      const handle = this.horizontalSlider.querySelector('.customized-product-model__slider-handle');
      if (handle) {
        handle.style.left = '50%';
        handle.style.transform = 'translateY(-50%) translateX(-50%)';
      }
      this.horizontalSlider.setAttribute('aria-valuenow', '50');
    }

    // Reset image size
    this.imageSize = DEFAULT_IMAGE_SIZE;
    if (this.imageSizeValue) {
      this.imageSizeValue.value = String(DEFAULT_IMAGE_SIZE);
    }
    if (this.imageSizeSlider) {
      const handle = this.imageSizeSlider.querySelector('.customized-product-model__slider-handle');
      if (handle) {
        const percentage = ((DEFAULT_IMAGE_SIZE - MIN_IMAGE_SIZE) / (MAX_IMAGE_SIZE - MIN_IMAGE_SIZE)) * 100;
        handle.style.left = `${percentage}%`;
        handle.style.transform = 'translateY(-50%) translateX(-50%)';
      }
      this.imageSizeSlider.setAttribute('aria-valuenow', String(DEFAULT_IMAGE_SIZE));
    }
    if (this.imageElement) {
      this.imageElement.style.width = '100%';
      this.imageElement.style.height = 'auto';
    }

    // Reset advanced options visibility
    this.showAdvancedOptions = false;
    if (this.verticalSliderWrapper) {
      this.verticalSliderWrapper.style.display = 'none';
    }
    if (this.horizontalSliderWrapper) {
      this.horizontalSliderWrapper.style.display = 'none';
    }
    if (this.lineSpacingWrapper) {
      this.lineSpacingWrapper.style.display = 'none';
    }
    if (this.imageSizeWrapper) {
      this.imageSizeWrapper.style.display = 'none';
    }
    if (this.advancedOptionsButton) {
      this.advancedOptionsButton.classList.remove('customized-button--selected');
      this.advancedOptionsButton.classList.add('customized-button--noselected');
    }

    // Hide line 2 container, show add line button
    if (this.line2Container) {
      this.line2Container.setAttribute('hidden', '');
    }
    if (this.addLineWrapper) {
      this.addLineWrapper.removeAttribute('hidden');
    }

    // Reset font dropdown selected states
    const fontDropdowns = this.querySelectorAll('.font-dropdown');
    fontDropdowns.forEach(dropdown => {
      const items = dropdown.querySelectorAll('.font-dropdown__item');
      items.forEach(item => {
        if (item.dataset.fontValue === 'allura') {
          item.classList.add('selected');
        } else {
          item.classList.remove('selected');
        }
      });
    });

    // Update overlays
    this.updateOverlaySizes();
    this.updateOverlayCurvature();
    this.updateLineSpacing();

    // Clear preview blob and file input, and discard any in-flight generation
    this.previewGenerationId++;
    this.previewBlob = null;
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  /**
   * Update overlay text based on personalization input
   * @param {string} type - Type of text ('line1' or 'line2')
   * @param {string} value - The input value
   */
  updateOverlayText(type, value) {
    const overlayElement = type === 'line1' ? this.overlayLine1 : this.overlayLine2;
    const textPathElement = type === 'line1' ? this.overlayLine1TextPath : this.overlayLine2TextPath;
    
    if (overlayElement && textPathElement) {
      textPathElement.textContent = value.trim();
      
      // Update empty state
      if (value.trim()) {
        overlayElement.setAttribute('data-empty', 'false');
      } else {
        overlayElement.setAttribute('data-empty', 'true');
      }
    }
    
    // Recalculate positions since content changed
    this.updateLineSpacing();
    
    // Generate preview image when text changes
    this.generatePreviewImage();
  }

  /**
   * Update overlay font based on font selection
   * @param {string} line - The line to update ('line1' or 'line2')
   * @param {string} font - The selected font name
   */
  updateOverlayFont(line, font) {
    const fontMap = {
      'caveat': 'Caveat, cursive',
      'carattere': 'Carattere, cursive',
      'alfresco': 'Al Fresco, cursive',
      'inkfree': 'Ink Free, cursive',
      'allura': 'Allura, cursive'
    };
    
    const fontFamily = fontMap[font] || 'Caveat, cursive';
    
    if (line === 'line1' && this.overlayLine1) {
      this.overlayLine1.setAttribute('font-family', fontFamily);
    } else if (line === 'line2' && this.overlayLine2) {
      this.overlayLine2.setAttribute('font-family', fontFamily);
    }
  }

  /**
   * Update overlay bold based on bold checkbox
   * @param {string} line - The line to update ('line1' or 'line2')
   * @param {boolean} isBold - Whether the text should be bold
   */
  updateOverlayBold(line, isBold) {
    const fontWeight = isBold ? '700' : '400';
    
    if (line === 'line1' && this.overlayLine1) {
      this.overlayLine1.setAttribute('font-weight', fontWeight);
    } else if (line === 'line2' && this.overlayLine2) {
      this.overlayLine2.setAttribute('font-weight', fontWeight);
    }
  }

  /**
   * Calculate font size percentage based on size input
   * Centralized formula used for both preview overlay and canvas generation
   * @param {number} size - Size value (1-40)
   * @returns {number} Font size as percentage of container width (e.g., 0.10 for 10%)
   */
  calculateFontSizePercent(size) {
    // Size scale (increased to match original implementation):
    // Size 1 → 5% of width
    // Size 40 → 50% of width
    return 5 + (size - 1) * (45 / 39);
  }

  /**
   * Update overlay text sizes based on size inputs
   */
  updateOverlaySizes() {
    // Get size values from inputs with proper defaults
    let line1Size = DEFAULT_LINE1_SIZE;
    let line2Size = DEFAULT_LINE2_SIZE;
    
    if (this.inputSize1?.value) {
      const parsed = parseInt(this.inputSize1.value);
      if (!isNaN(parsed)) line1Size = parsed;
    }
    
    if (this.inputSize2?.value) {
      const parsed = parseInt(this.inputSize2.value);
      if (!isNaN(parsed)) line2Size = parsed;
    }
    
    // Use centralized formula to calculate font size percentage
    const line1FontSizePercent = this.calculateFontSizePercent(line1Size) / 100;
    const line2FontSizePercent = this.calculateFontSizePercent(line2Size) / 100;
    
    // For SVG text, we use viewBox-relative sizing
    // The viewBox is VIEWBOX_WIDTH units wide, so we calculate font size as percentage of that
    const line1FontSize = Math.round(VIEWBOX_WIDTH * line1FontSizePercent);
    const line2FontSize = Math.round(VIEWBOX_WIDTH * line2FontSizePercent);
    
    if (this.overlayLine1) {
      this.overlayLine1.setAttribute('font-size', line1FontSize);
    }
    if (this.overlayLine2) {
      this.overlayLine2.setAttribute('font-size', line2FontSize);
    }
    
    // Recalculate positions since sizes changed (affects centering)
    this.updateLineSpacing();
  }

  /**
   * Calculate dynamic Y positions for lines based on content
   * Centers the text block vertically, accounting for whether there's one or two lines
   * @param {number} viewBoxHeight - Height of the viewBox (default: VIEWBOX_HEIGHT, matching 1:1 aspect ratio)
   * @returns {Object} Object with line1Y and line2Y positions
   */
  calculateLinePositions(viewBoxHeight = VIEWBOX_HEIGHT) {
    const hasLine1 = this.inputLine1?.value.trim();
    const hasLine2 = this.inputLine2?.value.trim();
    
    // Get font sizes to estimate line heights
    const line1Size = parseInt(this.inputSize1?.value) || DEFAULT_LINE1_SIZE;
    const line2Size = parseInt(this.inputSize2?.value) || DEFAULT_LINE2_SIZE;
    const line1Height = Math.round(viewBoxHeight * this.calculateFontSizePercent(line1Size) / 100);
    const line2Height = Math.round(viewBoxHeight * this.calculateFontSizePercent(line2Size) / 100);
    
    // Default gap between lines as percentage of viewBox height
    // ~1% of viewBox height provides good spacing
    const baseLineGap = viewBoxHeight * 0.01;
    
    // User spacing adjustment scaled relative to viewBox
    // lineSpacing ranges from -40 to +40, scale by ~0.85% of viewBox height per unit
    const spacingOffset = this.lineSpacing * (viewBoxHeight * 0.017);
    const actualLineGap = baseLineGap + spacingOffset;
    
    // Center of viewBox
    const centerY = viewBoxHeight / 2;
    
    if (hasLine1 && hasLine2) {
      // Two lines: center the block containing both
      const totalHeight = line1Height / 2 + actualLineGap + line2Height / 2;
      const blockTop = centerY - totalHeight / 2;
      const blockBottom = centerY + totalHeight / 2;
      return {
        line1Y: Math.round(blockTop + line1Height / 2),
        line2Y: Math.round(blockBottom + line2Height / 4),
      };
    } else if (hasLine1) {
      // Only line 1: center it
      return {
        line1Y: centerY + line1Height / 4,
        line2Y: centerY + line1Height / 4 + actualLineGap + line2Height
      };
    } else {
      // Only line 2 or no lines: center line 2
      return {
        line1Y: centerY - line2Height / 4 - actualLineGap,
        line2Y: centerY + line2Height / 4,
      };
    }
  }

  /**
   * Update line spacing by adjusting the vertical position of line 2's curve
   */
  updateLineSpacing() {
    // Update curve paths with new spacing
    this.updateCurvePathPositions();
  }

  /**
   * Update overlay curvature by adjusting the curve paths
   */
  updateOverlayCurvature() {
    // Get curvature values from hidden inputs with proper defaults
    let curvature1 = this.curvature1 || 0;
    let curvature2 = this.curvature2 || 0;
    
    if (this.curvatureValue1?.value) {
      const parsed = parseInt(this.curvatureValue1.value);
      if (!isNaN(parsed)) curvature1 = parsed;
    }
    
    if (this.curvatureValue2?.value) {
      const parsed = parseInt(this.curvatureValue2.value);
      if (!isNaN(parsed)) curvature2 = parsed;
    }
    
    // Update state
    this.curvature1 = curvature1;
    this.curvature2 = curvature2;
    
    // Update curve paths with new curvature
    this.updateCurvePathPositions();
  }

  /**
   * Update curve path positions based on vertical/horizontal offsets, curvature, and dynamic positioning
   */
  updateCurvePathPositions() {
    // Use dynamic positions that account for content
    const positions = this.calculateLinePositions();
    
    // Calculate offsets from position sliders
    // Vertical offset: MIN_POSITION_OFFSET-MAX_POSITION_OFFSET range, center at DEFAULT_POSITION_OFFSET
    // Map to viewBox coordinates (VIEWBOX_WIDTH units)
    const verticalShift = (this.verticalOffset - DEFAULT_POSITION_OFFSET) * (VIEWBOX_HEIGHT * 0.01); // 1% per unit offset
    const horizontalShift = (this.horizontalOffset - DEFAULT_POSITION_OFFSET) * (VIEWBOX_WIDTH * 0.01); // 2% per unit offset
    
    // Update curve paths with curvature and position
    if (this.curvePath1) {
      const baseY = positions.line1Y + verticalShift;
      const controlY = baseY - (this.curvature1 || 0) * 10;
      const startX = 50 + horizontalShift;
      const midX = 500 + horizontalShift;
      const endX = 950 + horizontalShift;
      this.curvePath1.setAttribute('d', `M ${startX} ${baseY} Q ${midX} ${controlY}, ${endX} ${baseY}`);
    }
    
    if (this.curvePath2) {
      const baseY = positions.line2Y + verticalShift;
      const controlY = baseY - (this.curvature2 || 0) * 10;
      const startX = 50 + horizontalShift;
      const midX = 500 + horizontalShift;
      const endX = 950 + horizontalShift;
      this.curvePath2.setAttribute('d', `M ${startX} ${baseY} Q ${midX} ${controlY}, ${endX} ${baseY}`);
    }
  }

  /**
   * Load font and generate preview with loading spinner
   * @param {string} font - The font to load
   */
  async loadFontAndGeneratePreview(font) {
    const fontFamily = this.getFontFamily(font);
    
    // Show loading spinner
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.remove('hidden');
    }
    
    // Hide overlay text while loading
    if (this.overlayLine1) this.overlayLine1.style.opacity = '0';
    if (this.overlayLine2) this.overlayLine2.style.opacity = '0';
    
    try {
      // Wait for font to load (with timeout)
      await Promise.race([
        document.fonts.load(`500 120px ${fontFamily}`),
        document.fonts.load(`400 80px ${fontFamily}`),
        new Promise(resolve => setTimeout(resolve, 3000)) // 3s timeout
      ]);
      
      // Small delay to ensure font is fully loaded
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Error loading font:', error);
    }

    // Show overlay text again (even if font loading failed) and hide loading spinner
    if (this.overlayLine1) this.overlayLine1.style.opacity = '';
    if (this.overlayLine2) this.overlayLine2.style.opacity = '';
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.add('hidden');
    }

    await this.generatePreviewImage();
  }

  /**
   * Load font file and convert to base64 data URL
   * @param {string} fontName - Font identifier
   * @returns {Promise<string>} Base64 data URL
   */
  async loadFontAsDataURL(fontName) {
    // Check cache first
    if (this.fontDataCache[fontName]) {
      return this.fontDataCache[fontName];
    }

    // Get the font URL from the data attribute (populated by Liquid's asset_url filter)
    const fontUrl = this.fontUrls[fontName];
    if (!fontUrl) {
      console.warn('Unknown font or missing font URL:', fontName);
      return '';
    }

    try {
      // Fetch the font file
      const response = await fetch(fontUrl);
      const blob = await response.blob();
      
      // Convert to base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result;
          this.fontDataCache[fontName] = dataUrl;
          resolve(dataUrl);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading font:', fontName, error);
      return '';
    }
  }

  /**
   * Convert image URL to base64 data URL
   * @param {string} imageUrl - URL of the image to convert
   * @returns {Promise<string>} Base64 data URL of the image
   */
  async loadImageAsDataURL(imageUrl) {
    try {
      // Fetch the image
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Convert to base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading image as data URL:', imageUrl, error);
      return '';
    }
  }

  /**
   * Generate preview SVG with background image and text overlay
   * Extracts the overlay SVG from the DOM and combines it with the background image
   */
  async generatePreviewImage() {
    if (!this.imageElement || !this.overlaySvg) return;

    const generationId = ++this.previewGenerationId;
    const line1Text = this.inputLine1?.value.trim() || '';
    const line2Text = this.inputLine2?.value.trim() || '';

    // Only generate if there's text to display
    if (!line1Text && !line2Text) {
      this.previewBlob = null;
      return;
    }

    try {
      // Wait for image to load
      await this.waitForImageLoad();

      // Convert background image to base64 data URL for embedding
      const backgroundImageDataUrl = await this.loadImageAsDataURL(this.imageElement.src);
      
      // Load font data for embedding in SVG
      const fonts = new Set();
      if (line1Text) fonts.add(this.currentFont1);
      if (line2Text) fonts.add(this.currentFont2);
      
      const fontDataUrls = {};
      for (const font of fonts) {
        fontDataUrls[font] = await this.loadFontAsDataURL(font);
      }

      // A newer generation started while we were fetching: drop this stale result.
      // Everything below is synchronous, so no other generation can interleave.
      if (generationId !== this.previewGenerationId) return;

      // Clone the overlay SVG element
      const svgClone = this.overlaySvg.cloneNode(true);

      // Drop the transient inline opacity set while fonts load, it would override data-empty in the export
      svgClone.querySelectorAll('.customized-product-model__overlay-text').forEach((text) => text.removeAttribute('style'));

      // Set explicit dimensions
      svgClone.setAttribute('width', this.generatedFileWidth);
      svgClone.setAttribute('height', this.generatedFileHeight);
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      
      // Get the defs element or create one
      let defs = svgClone.querySelector('defs');
      if (!defs) {
        defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        svgClone.insertBefore(defs, svgClone.firstChild);
      }
      
      // Add embedded fonts to defs
      const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      let cssContent = '';
      
      for (const [font, dataUrl] of Object.entries(fontDataUrls)) {
        if (dataUrl) {
          const fontName = this.getFontFamily(font, false);
          cssContent += `@font-face { font-family: '${fontName}'; src: url('${dataUrl}') format('truetype'); font-weight: normal; font-style: normal; }\n`;
        }
      }
      
      // Add CSS for text styling
      cssContent += `
        .customized-product-model__overlay-text {
          fill: #3371ba;
          transition: opacity 0.3s ease, font-family 0.2s ease, font-weight 0.2s ease;
        }
        .customized-product-model__overlay-text[data-empty="true"] {
          opacity: 0;
        }
        .customized-product-model__overlay-text[data-empty="false"] {
          opacity: 1;
        }
      `;
      
      style.textContent = cssContent;
      defs.appendChild(style);
      
      // Add background image as first child after defs
      // Scale the image based on the imageSize percentage and center it
      const scaledWidth = this.generatedFileWidth * (this.imageSize / 100);
      const scaledHeight = this.generatedFileHeight * (this.imageSize / 100);
      const offsetX = (this.generatedFileWidth - scaledWidth) / 2;
      const offsetY = (this.generatedFileHeight - scaledHeight) / 2;
      
      const backgroundImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      backgroundImage.setAttribute('href', backgroundImageDataUrl);
      backgroundImage.setAttribute('width', scaledWidth);
      backgroundImage.setAttribute('height', scaledHeight);
      backgroundImage.setAttribute('x', offsetX);
      backgroundImage.setAttribute('y', offsetY);
      backgroundImage.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      
      // Insert background image right after defs
      const firstNonDefs = Array.from(svgClone.children).find(child => child.tagName !== 'defs');
      if (firstNonDefs) {
        svgClone.insertBefore(backgroundImage, firstNonDefs);
      } else {
        svgClone.appendChild(backgroundImage);
      }
      
      // Serialize the SVG to string
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgClone);
      
      // Convert SVG string directly to blob
      this.previewBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });

    } catch (error) {
      console.error('Error generating preview SVG:', error);
      if (generationId === this.previewGenerationId) {
        this.previewBlob = null;
      }
    }
  }

  /**
   * Download the preview SVG file
   */
  downloadPreviewImage() {
    if (!this.previewBlob) return;

    // Create a download link
    const url = URL.createObjectURL(this.previewBlob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const line1Text = this.inputLine1?.value.trim() || 'preview';
    const sanitizedLine1 = line1Text.replace(/[^a-zA-Z0-9]/g, '_');
    link.download = `personnalisation-${sanitizedLine1}-${timestamp}.svg`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Wait for image to be fully loaded
   */
  waitForImageLoad() {
    return new Promise((resolve, reject) => {
      if (this.imageElement.complete && this.imageElement.naturalHeight !== 0) {
        resolve();
      } else {
        this.imageElement.onload = () => resolve();
        this.imageElement.onerror = () => reject(new Error('Image failed to load'));
        // Timeout after 5 seconds
        setTimeout(() => reject(new Error('Image load timeout')), 5000);
      }
    });
  }

  /**
   * Get font family name with fallback
   * @param {string} font - Font identifier
   * @param {boolean} withFallback - Whether to include fallback (default: true)
   * @returns {string} Font family string
   */
  getFontFamily(font, withFallback = true) {
    const fontMap = {
      'caveat': 'Caveat',
      'carattere': 'Carattere',
      'alfresco': 'Al Fresco',
      'inkfree': 'Ink Free',
      'allura': 'Allura'
    };
    const fontName = fontMap[font] || 'Allura';
    return withFallback ? `${fontName}, cursive` : fontName;
  }

  /**
   * Create the file input element and add it to the form
   */
  createFileInput() {
    const productFormID = `product-form-${this.sectionId}`;
    const productForm = document.getElementById(productFormID);
    
    if (!productForm) {
      console.warn('CustomizedProductModel: Could not find product form with ID:', productFormID);
      return;
    }

    // Create file input for the preview
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.name = 'properties[Personnalisation]';
    this.fileInput.setAttribute('form', productFormID);
    this.fileInput.style.display = 'none';
    this.fileInput.style.position = 'absolute';
    this.fileInput.style.pointerEvents = 'none';
    
    // Add to form
    productForm.appendChild(this.fileInput);
  }

  /**
   * Add preview image to file input if personalization exists
   */
  addPreviewImageToForm() {
    const hasPersonalization = this.inputLine1?.value.trim() || this.inputLine2?.value.trim();
    
    if (this.previewBlob && hasPersonalization && this.fileInput) {
      try {
        // Create a File object from the SVG blob
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const line1Text = this.inputLine1?.value.trim() || 'preview';
        const sanitizedLine1 = line1Text.replace(/[^a-zA-Z0-9]/g, '_');
        const filename = `personnalisation-${sanitizedLine1}-${timestamp}.svg`;
        
        const file = new File([this.previewBlob], filename, { type: 'image/svg+xml' });
        
        // Use DataTransfer to set the file on the input
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        this.fileInput.files = dataTransfer.files;

        // Trigger download of the preview image
        // this.downloadPreviewImage();
      } catch (error) {
        console.error('Error adding preview to form:', error);
      }
    }
  }

  /**
   * Set up form submission to include preview image
   */
  setupFormSubmission() {
    const productFormID = `product-form-${this.sectionId}`;
    const productForm = document.getElementById(productFormID);
    
    if (!productForm) {
      console.warn('CustomizedProductModel: Could not find product form with ID:', productFormID);
      return;
    }

    // Listen to form submit event with capture phase to run before product-form's handler
    productForm.addEventListener('submit', async (e) => {
      this.addPreviewImageToForm();
      // Let the form submission continue naturally (product-form will handle it)
    }, true); // Use capture phase to ensure we run first

    // Also handle accelerated checkout (Shop Pay, Apple Pay, etc.)
    const acceleratedCheckout = productForm.querySelector('shopify-accelerated-checkout');
    if (acceleratedCheckout) {
      acceleratedCheckout.addEventListener('click', (e) => {
        this.addPreviewImageToForm();
      }, true); // Use capture phase to ensure we run first
    }
  }
}

// Define the custom element
customElements.define('customized-product-model', CustomizedProductModel);

